import { useState, type JSX } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Link as RouterLink } from 'react-router-dom'
import Alert from '@mui/material/Alert'
import Button from '@mui/material/Button'
import Card from '@mui/material/Card'
import Chip from '@mui/material/Chip'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogContentText from '@mui/material/DialogContentText'
import DialogTitle from '@mui/material/DialogTitle'
import Link from '@mui/material/Link'
import ListItemText from '@mui/material/ListItemText'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import Stack from '@mui/material/Stack'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Typography from '@mui/material/Typography'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import PersonAddOutlinedIcon from '@mui/icons-material/PersonAddOutlined'
import {
  AUDIT_ACTION_SPEC,
  type InviteUsersResponse,
  type PendingInvite,
  type UserChange,
  type UserSummary,
} from '@profiler/shared'
import { useApi, useSignOut } from '../auth'
import { PageHeader } from '../components/Page'
import { EmptyState, ErrorPanel, LoadingPanel } from '../components/States'
import { EM_DASH, formatDate, formatDateTime } from '../format'
import { qk, useMe, useUsers } from '../queries'
import { usePageTitle } from '../usePageTitle'
import { InviteDialog } from './users/InviteDialog'
import { JoiningNotice } from './users/JoiningNotice'
import { PendingInvites } from './users/PendingInvites'

/**
 * Who can use Quarry, and with what rights.
 *
 * Somebody new is invited here, and appears in the list after their first
 * sign-in. Whether the invitation alone lets them in, or Microsoft Entra must
 * assign them too, is the deployment's joining mode, which the notice at the
 * top says. Everything after that is here: making somebody an administrator,
 * taking it away, and cutting somebody off.
 *
 * The screen holds none of the rules. Each row arrives with the changes the
 * server would refuse and why, and the menu greys those out with the reason
 * underneath, so what is offered is exactly what will be accepted.
 */

interface ChangeCopy {
  /** The menu item. */
  label: string
  /** The dialog's question. */
  title: (name: string) => string
  /** What will happen, in the terms of the person it happens to. */
  body: string
  confirm: string
  tone: 'primary' | 'error'
  /** What the screen says once it is done. */
  done: (name: string) => string
  /** The question put another way, for the one change somebody can make to themselves. */
  self?: { title: string; body: string }
}

/**
 * A role travels in the token, and a token lasts an hour.
 *
 * Said on every role change, because "I made her an admin and she still cannot
 * see Settings" is the one question this screen will otherwise produce.
 */
const ROLE_TIMING = 'It reaches them within the hour, or straight away if they sign out and back in.'

const COPY: Record<UserChange, ChangeCopy> = {
  promote: {
    label: 'Make administrator',
    title: (name) => `Make ${name} an administrator?`,
    body: `Administrators change Settings and spending limits, read the audit log and manage users. ${ROLE_TIMING}`,
    confirm: 'Make administrator',
    tone: 'primary',
    done: (name) => `${name} is now an administrator. ${ROLE_TIMING}`,
  },
  demote: {
    label: 'Remove administrator rights',
    title: (name) => `Remove ${name}'s administrator rights?`,
    body: `They keep using Quarry as a member. ${ROLE_TIMING}`,
    confirm: 'Remove rights',
    tone: 'error',
    done: (name) => `${name} is now a member. ${ROLE_TIMING}`,
  },
  disable: {
    label: 'Disable',
    title: (name) => `Disable ${name}?`,
    body: 'They are signed out everywhere on their next click, and cannot sign in again until an administrator re-enables them. Their invitation is withdrawn too. Nothing they did is removed.',
    confirm: 'Disable',
    tone: 'error',
    done: (name) => `${name} is disabled and signed out everywhere.`,
  },
  enable: {
    label: 'Re-enable',
    title: (name) => `Re-enable ${name}?`,
    body: 'They can sign in again the way they did before, with the same role.',
    confirm: 'Re-enable',
    tone: 'primary',
    done: (name) => `${name} can sign in again.`,
  },
  signout: {
    label: 'Sign out everywhere',
    title: (name) => `Sign ${name} out everywhere?`,
    body: 'Every browser they are signed in on, a lost laptop included, is signed out on its next click. They can sign straight back in on a device they still have.',
    confirm: 'Sign out everywhere',
    tone: 'error',
    done: (name) => `${name} is signed out everywhere.`,
    self: {
      title: 'Sign yourself out everywhere?',
      body: 'Every browser you are signed in on, a lost laptop included, is signed out on its next click.',
    },
  },
}

/** What can be done to this person at all, before the rules say whether it is allowed. */
export function applicableChanges(user: UserSummary): UserChange[] {
  if (!user.enabled) return ['enable']
  return [user.role === 'admin' ? 'demote' : 'promote', 'signout', 'disable']
}

function RoleChip({ role }: Readonly<{ role: UserSummary['role'] }>): JSX.Element {
  return role === 'admin' ? (
    <Chip size="small" color="primary" label="Administrator" />
  ) : (
    <Chip size="small" variant="outlined" label="Member" />
  )
}

function StatusCell({ user }: Readonly<{ user: UserSummary }>): JSX.Element {
  return (
    <>
      {user.enabled ? (
        <Chip size="small" variant="outlined" color="success" label="Active" />
      ) : (
        <Chip size="small" color="error" label="Disabled" />
      )}
      {user.sessionsEndedAt ? (
        <Typography
          variant="caption"
          color="text.secondary"
          component="div"
          sx={{ mt: 0.5, whiteSpace: 'nowrap' }}
        >
          Signed out everywhere
          <br />
          {formatDateTime(user.sessionsEndedAt)}
        </Typography>
      ) : null}
    </>
  )
}

/**
 * When somebody first signed in, or as much as is known of it.
 *
 * Cognito records the moment for somebody who signs in with Microsoft, since
 * that is when it creates them, and nothing of the kind for a password account.
 * So a password account that has been used says "Not recorded" and gives its
 * creation date, rather than passing that date off as a sign-in.
 */
function FirstSignIn({ user }: Readonly<{ user: UserSummary }>): JSX.Element {
  if (user.firstSignInAt) return <>{formatDate(user.firstSignInAt)}</>
  if (user.pendingFirstSignIn) return <>Never</>
  return (
    <>
      Not recorded
      {user.createdAt ? (
        <Typography variant="caption" color="text.secondary" component="div">
          Created {formatDate(user.createdAt)}
        </Typography>
      ) : null}
    </>
  )
}

function LastAction({ last }: Readonly<{ last: UserSummary['lastAction'] }>): JSX.Element {
  if (!last) return <>{EM_DASH}</>
  return (
    <>
      <Typography variant="body2">{AUDIT_ACTION_SPEC[last.action]?.label ?? last.action}</Typography>
      <Typography variant="caption" color="text.secondary" component="div" sx={{ whiteSpace: 'nowrap' }}>
        {formatDateTime(last.at)}
      </Typography>
    </>
  )
}

function UserRow({
  user,
  onChoose,
}: Readonly<{ user: UserSummary; onChoose: (user: UserSummary, change: UserChange) => void }>): JSX.Element {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null)
  return (
    <TableRow hover>
      <TableCell>
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="body2" fontWeight={600}>
            {user.name}
          </Typography>
          {user.isYou ? <Chip size="small" variant="outlined" label="You" /> : null}
        </Stack>
        <Typography variant="caption" color="text.secondary" component="div">
          {user.email}
          {/* The break-glass kind of account, which is worth noticing rather than assuming. */}
          {user.signIn === 'password' ? ' · password account' : ''}
        </Typography>
      </TableCell>
      <TableCell>
        <RoleChip role={user.role} />
      </TableCell>
      <TableCell>
        <StatusCell user={user} />
      </TableCell>
      <TableCell sx={{ whiteSpace: 'nowrap' }}>
        <FirstSignIn user={user} />
      </TableCell>
      <TableCell>
        <LastAction last={user.lastAction} />
      </TableCell>
      <TableCell sx={{ whiteSpace: 'nowrap' }}>
        {user.email ? (
          // `who` is the Audit screen's own filter key, so this opens it already narrowed.
          <Link component={RouterLink} to={`/audit?who=${encodeURIComponent(user.email)}`}>
            Audit trail
          </Link>
        ) : (
          EM_DASH
        )}
      </TableCell>
      <TableCell align="right">
        <Button
          size="small"
          endIcon={<ExpandMoreIcon />}
          aria-haspopup="menu"
          aria-label={`Manage ${user.name}`}
          onClick={(event) => setAnchor(event.currentTarget)}
        >
          Manage
        </Button>
        <Menu anchorEl={anchor} open={anchor !== null} onClose={() => setAnchor(null)}>
          {applicableChanges(user).map((change) => {
            const refused = user.refusals[change]
            /*
             * `aria-disabled` rather than `disabled`.
             *
             * MUI's disabled item fades everything in it, and the reason is the
             * one part of the row somebody needs to read. This way the label is
             * greyed, the reason keeps its contrast, and the item stays
             * focusable, so a screen reader reaches the reason too.
             */
            return (
              <MenuItem
                key={change}
                aria-disabled={refused === undefined ? undefined : true}
                onClick={
                  refused === undefined
                    ? () => {
                        setAnchor(null)
                        onChoose(user, change)
                      }
                    : undefined
                }
                sx={
                  refused === undefined
                    ? undefined
                    : { cursor: 'default', '&:hover': { bgcolor: 'transparent' } }
                }
              >
                <ListItemText
                  primary={COPY[change].label}
                  secondary={refused}
                  slotProps={{ primary: refused === undefined ? {} : { color: 'text.disabled' } }}
                />
              </MenuItem>
            )
          })}
        </Menu>
      </TableCell>
    </TableRow>
  )
}

interface Pending {
  user: UserSummary
  change: UserChange
}

function ConfirmChange({
  open,
  pending,
  busy,
  error,
  onCancel,
  onConfirm,
}: Readonly<{
  open: boolean
  /** Kept after the dialog closes, so it does not empty itself while it fades out. */
  pending: Pending | null
  busy: boolean
  error: unknown
  onCancel: () => void
  onConfirm: () => void
}>): JSX.Element {
  const copy = pending ? COPY[pending.change] : null
  const self = pending?.user.isYou ? copy?.self : undefined
  return (
    <Dialog open={open} onClose={busy ? undefined : onCancel} maxWidth="xs" fullWidth>
      {pending && copy ? (
        <>
          <DialogTitle>{self?.title ?? copy.title(pending.user.name)}</DialogTitle>
          <DialogContent>
            <DialogContentText>{self?.body ?? copy.body}</DialogContentText>
            {pending.change === 'signout' && pending.user.isYou ? (
              <Alert severity="warning" sx={{ mt: 2 }}>
                That includes this browser, so you will be asked to sign in again.
              </Alert>
            ) : null}
            {error ? (
              <Alert severity="error" sx={{ mt: 2 }}>
                {error instanceof Error ? error.message : String(error)}
              </Alert>
            ) : null}
          </DialogContent>
          <DialogActions>
            <Button onClick={onCancel} disabled={busy}>
              Cancel
            </Button>
            <Button variant="contained" color={copy.tone} onClick={onConfirm} disabled={busy}>
              {copy.confirm}
            </Button>
          </DialogActions>
        </>
      ) : null}
    </Dialog>
  )
}

function UsersTable({
  users,
  onChoose,
}: Readonly<{
  users: UserSummary[]
  onChoose: (user: UserSummary, change: UserChange) => void
}>): JSX.Element {
  return (
    <Card>
      <TableContainer>
        {/* Scrolls sideways on a phone rather than crushing each column to a word a line. */}
        <Table size="small" sx={{ minWidth: 900 }}>
          <TableHead>
            {/* Headers never wrap: a two-line "First signed in" pushes every row down. */}
            <TableRow sx={{ '& th': { whiteSpace: 'nowrap' } }}>
              <TableCell>Person</TableCell>
              <TableCell>Role</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>First signed in</TableCell>
              <TableCell>Last action</TableCell>
              <TableCell>History</TableCell>
              <TableCell align="right" aria-label="Actions" />
            </TableRow>
          </TableHead>
          <TableBody>
            {users.map((user) => (
              <UserRow key={user.sub} user={user} onChoose={onChoose} />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Card>
  )
}

/** "9 people, 2 administrators" — enough to see at a glance that there is more than one of the second. */
function headcount(users: readonly UserSummary[], invites: readonly PendingInvite[]): string {
  const active = users.filter((u) => u.enabled)
  const admins = active.filter((u) => u.role === 'admin').length
  const disabled = users.length - active.length
  const parts = [
    `${active.length} ${active.length === 1 ? 'person' : 'people'}`,
    `${admins} ${admins === 1 ? 'administrator' : 'administrators'}`,
  ]
  if (disabled > 0) parts.push(`${disabled} disabled`)
  if (invites.length > 0) parts.push(`${invites.length} invited`)
  return parts.join(', ')
}

/**
 * What an invitation did, address by address, in one paragraph.
 *
 * Only the first group changed anything. The others are answered rather than
 * silently skipped, because each needs something different from the person
 * who pasted it.
 */
export function inviteSummary(outcomes: InviteUsersResponse['outcomes']): string {
  const of = (kind: string): string[] => outcomes.filter((o) => o.outcome === kind).map((o) => o.email)
  const invited = of('invited')
  const parts: string[] = []
  if (invited.length > 0) {
    parts.push(`Invited ${invited.length === 1 ? invited[0] : `${invited.length} people`}.`)
  }
  const sent = outcomes.filter((o) => o.emailed === 'sent')
  const unsent = outcomes.filter((o) => o.emailed === 'failed').map((o) => o.email)
  if (sent.length > 0) {
    parts.push(
      sent.length === invited.length ? 'Each was emailed the sign-in link.' : `Emailed ${sent.length}.`,
    )
  }
  if (unsent.length > 0) {
    parts.push(
      `The email to ${unsent.join(', ')} could not be sent; the invitation stands. Use Resend email below, or tell them yourself.`,
    )
  }
  const already = of('alreadyInvited')
  if (already.length > 0) parts.push(`Already invited: ${already.join(', ')}.`)
  const members = of('alreadyMember')
  if (members.length > 0) parts.push(`Already using Quarry: ${members.join(', ')}.`)
  const disabled = of('memberDisabled')
  if (disabled.length > 0) {
    parts.push(`Disabled, so re-enable them rather than inviting: ${disabled.join(', ')}.`)
  }
  return parts.join(' ')
}

export function UsersPage(): JSX.Element {
  usePageTitle('Users')

  const api = useApi()
  const signOut = useSignOut()
  const queryClient = useQueryClient()
  const me = useMe()
  const isAdmin = me.data?.isAdmin === true
  const users = useUsers(isAdmin)

  const [pending, setPending] = useState<Pending | null>(null)
  const [confirming, setConfirming] = useState(false)
  const [inviting, setInviting] = useState(false)
  const [done, setDone] = useState<string | null>(null)
  const [problem, setProblem] = useState<string | null>(null)

  const invite = useMutation({
    mutationFn: ({ emails, sendEmail }: { emails: string[]; sendEmail: boolean }) =>
      api.inviteUsers(emails, sendEmail),
    onSuccess: ({ outcomes, ...list }) => {
      queryClient.setQueryData(qk.users, list)
      void queryClient.invalidateQueries({ queryKey: qk.audit })
      setInviting(false)
      setDone(inviteSummary(outcomes))
    },
  })

  const resendInvite = useMutation({
    mutationFn: (email: string) => api.resendInvite(email),
    onSuccess: (_, email) => {
      void queryClient.invalidateQueries({ queryKey: qk.audit })
      setDone(`Emailed the sign-in link to ${email} again.`)
    },
    onError: (err, email) => {
      setProblem(`The email to ${email} was not sent. ${err instanceof Error ? err.message : String(err)}`)
    },
  })

  const cancelInvite = useMutation({
    mutationFn: (email: string) => api.cancelInvite(email),
    onSuccess: (list, email) => {
      queryClient.setQueryData(qk.users, list)
      void queryClient.invalidateQueries({ queryKey: qk.audit })
      setDone(`The invitation for ${email} is cancelled.`)
    },
  })

  const change = useMutation({
    mutationFn: ({ user, change: what }: Pending) => api.changeUser(user.sub, what),
    onSuccess: (data, { user, change: what }) => {
      // The answer IS the list, with the rules worked out again for everybody,
      // so there is nothing to refetch.
      queryClient.setQueryData(qk.users, data)
      void queryClient.invalidateQueries({ queryKey: qk.audit })
      setConfirming(false)
      // Ending your own sessions ends this one too. Going through the normal
      // sign-out now beats waiting for the next click to be refused.
      if (what === 'signout' && user.isYou) {
        void signOut()
        return
      }
      setDone(COPY[what].done(user.name))
    },
  })

  const choose = (user: UserSummary, what: UserChange): void => {
    change.reset()
    setDone(null)
    setPending({ user, change: what })
    setConfirming(true)
  }

  if (me.isLoading) return <LoadingPanel label="Loading users" />
  // Not the permission wall: "we could not find out who you are" is a different
  // sentence from "you are not an administrator". See SettingsPage.
  if (me.isError) return <ErrorPanel error={me.error} onRetry={() => void me.refetch()} />
  if (!isAdmin) {
    return (
      <Card>
        <EmptyState
          title="Administrators only"
          description="Who can use Quarry, and with what rights, is changed by an administrator. Ask one if somebody needs access or has left."
        />
      </Card>
    )
  }

  const body = (): JSX.Element => {
    if (users.isLoading) return <LoadingPanel label="Loading users" />
    if (users.isError) return <ErrorPanel error={users.error} onRetry={() => void users.refetch()} />
    const list = users.data?.users ?? []
    const invites = users.data?.invites ?? []
    return (
      <>
        {users.data ? <JoiningNotice joining={users.data.joining} /> : null}
        {list.length === 0 ? (
          <Card>
            <EmptyState
              title="Nobody has signed in yet"
              description="People appear here after their first sign-in. Invite somebody, then ask them to sign in."
            />
          </Card>
        ) : (
          <UsersTable users={list} onChoose={choose} />
        )}
        <PendingInvites
          invites={invites}
          busy={cancelInvite.isPending}
          error={cancelInvite.error}
          onCancel={(email) => {
            cancelInvite.reset()
            setDone(null)
            return cancelInvite.mutateAsync(email)
          }}
          resending={resendInvite.isPending ? (resendInvite.variables ?? null) : null}
          onResend={(email) => {
            setDone(null)
            setProblem(null)
            resendInvite.mutate(email)
          }}
        />
      </>
    )
  }

  return (
    <Stack spacing={2}>
      <PageHeader
        title="Users"
        description="Everyone who uses Quarry, and everyone invited to. An invited person appears in the list after their first sign-in."
        actions={
          <Button
            variant="contained"
            startIcon={<PersonAddOutlinedIcon />}
            onClick={() => {
              invite.reset()
              setDone(null)
              setProblem(null)
              setInviting(true)
            }}
          >
            Invite people
          </Button>
        }
        meta={
          <Typography variant="caption" color="text.secondary">
            {users.data ? `${headcount(users.data.users, users.data.invites ?? [])}. ` : ''}A role change
            reaches the person within the hour. Disabling somebody or signing them out everywhere works on
            their next click.
          </Typography>
        }
      />
      {done ? (
        <Alert severity="success" onClose={() => setDone(null)}>
          {done}
        </Alert>
      ) : null}
      {problem ? (
        <Alert severity="error" onClose={() => setProblem(null)}>
          {problem}
        </Alert>
      ) : null}
      {body()}
      <InviteDialog
        open={inviting}
        joining={users.data?.joining ?? 'entra'}
        busy={invite.isPending}
        error={invite.error}
        onClose={() => setInviting(false)}
        onInvite={(emails, sendEmail) => invite.mutate({ emails, sendEmail })}
      />
      <ConfirmChange
        open={confirming}
        pending={pending}
        busy={change.isPending}
        error={change.error}
        onCancel={() => setConfirming(false)}
        onConfirm={() => {
          if (pending) change.mutate(pending)
        }}
      />
    </Stack>
  )
}
